import type { EmailTemplate } from "../../api/types";
import { Field } from "../../ui/components";

// The wording of a message is written the same way whether it opens an exchange or
// answers one, so both screens share these fields rather than each growing their own copy
// that drifts. A template and a typed body are deliberately exclusive: a rendered template
// and words a person typed have different safety rules, and the server enforces that.
export interface MessageDraft {
  templateKey: string;
  subject: string;
  textBody: string;
  scheduledAt: string;
}

export const emptyDraft: MessageDraft = {
  templateKey: "",
  subject: "",
  textBody: "",
  scheduledAt: "",
};

// A template carries its own category, and the server refuses one whose category does not
// match the lawful purpose of the send. Filtering here means the mismatch is never offered
// rather than reported after the person has written the whole message.
export function templatesForPurpose(templates: EmailTemplate[], purpose: string): EmailTemplate[] {
  const category = purpose === "marketing" ? "marketing" : "transactional";
  return templates.filter((template) => template.active && template.category === category);
}

export function MessageFields({
  templates,
  draft,
  onChange,
  disabled = false,
}: {
  templates: EmailTemplate[];
  draft: MessageDraft;
  onChange(draft: MessageDraft): void;
  disabled?: boolean | undefined;
}) {
  const usingTemplate = draft.templateKey !== "";
  const chosen = templates.find((template) => template.key === draft.templateKey);

  return (
    <>
      <Field
        label="Template"
        hint="Templates carry their own subject and wording, and are filled in with the recipient's own name when the message is sent."
      >
        <select
          name="templateKey"
          value={draft.templateKey}
          disabled={disabled}
          onChange={(event) => onChange({ ...draft, templateKey: event.target.value })}
        >
          <option value="">Write this message myself</option>
          {templates.map((template) => (
            <option key={template.key} value={template.key}>
              {template.key}
            </option>
          ))}
        </select>
      </Field>
      {usingTemplate ? (
        <Field
          label="Template subject"
          hint="Taken from the template. Change the wording on the templates screen if it is not right."
        >
          <input value={chosen?.subject ?? ""} readOnly disabled />
        </Field>
      ) : (
        <Field
          label="Subject"
          hint="Line breaks are refused: a subject carrying one can introduce another mail header."
        >
          <input
            name="subject"
            required
            maxLength={300}
            disabled={disabled}
            value={draft.subject}
            onChange={(event) => onChange({ ...draft, subject: event.target.value })}
          />
        </Field>
      )}
      {usingTemplate ? (
        <Field
          label="Template message"
          hint="A preview of the stored wording before it is filled in."
        >
          <textarea value={chosen?.textBody ?? ""} readOnly disabled rows={8} />
        </Field>
      ) : (
        <Field label="Message">
          <textarea
            name="textBody"
            required
            rows={8}
            disabled={disabled}
            value={draft.textBody}
            onChange={(event) => onChange({ ...draft, textBody: event.target.value })}
          />
        </Field>
      )}
      <Field
        label="Send at"
        hint="Leave empty to send now. A scheduled message can be canceled from the conversation up to the moment it goes, and cannot be parked more than a year ahead."
      >
        <input
          name="scheduledAt"
          type="datetime-local"
          disabled={disabled}
          value={draft.scheduledAt}
          onChange={(event) => onChange({ ...draft, scheduledAt: event.target.value })}
        />
      </Field>
    </>
  );
}

// The schedule field holds a local wall-clock time with no zone, which is what the person
// meant. Converting through Date reads it in the browser's own zone, and the server keeps
// the resulting instant rather than the text.
export function scheduleToISO(value: string): string | null {
  if (!value) return null;
  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return null;
  return moment.toISOString();
}
